import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TableCell } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import lodash from "lodash";

// COMPONENTS
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import DropdownButton from "@/main/components/shared/dropdownButton";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import { KOGTableRow } from "@/main/components/shared/boxGrid/styles";

// SERVICES
import { usergroupApi } from "@/main/api";
import { getOptionsStatus } from "@/main/services/data/data";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { UserGroupFilterRequest } from "@/main/models/request/usergroupRequest";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { StatusUserGroupEnum } from "@/main/services/enum/StatusUserGroupEnum";
import { getDefaultMessageResultsFound, getTranslateByKey, haveIgnoreElement } from "@/main/services/utils";

import * as S from "./styles";
import { TextEllipsis } from "@/main/components/shared/styles";
import { GroupResponse } from "@/main/models/response/usergroupResponse";
import { toast } from "react-toastify";

export const INITIAL_GRID_STATE: IGridData = {
	totalItems: 0,
	isLoading: false,
	data: [],
};

export interface IGridData {
	data: GroupResponse[];
	totalItems: number;
	isLoading: boolean;
}

interface IPropsUsergroupSearchGrid {
	gridData: IGridData;
	formFilters: UserGroupFilterRequest | null;
	setGridData: React.Dispatch<React.SetStateAction<IGridData>>;
}

function UsergroupSearchGrid({ gridData, formFilters, setGridData }: IPropsUsergroupSearchGrid) {
	// Hooks
	const navigate = useNavigate();
	const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState(false);
	const [hasShowMore, setHasShowMore] = useState(false);
	const [pageIndex, setPageIndex] = useState(PaginationEnum.PageIndexIntial);

	const optionsStatusUserGroup = getOptionsStatus();

	function handleRowBoxGrid(group: GroupResponse, event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) {
		if (haveIgnoreElement(event.target as HTMLElement)) return;
		if (group.hasOtherCompanyRelation) {
			toast.error(getTranslateByKey("information.companyAssociatedWithAnotherInsurance", true));
			return;
		}
		navigate(
			`${RoutesConstants.registrationRegisterUsergroup}?usergroupGuid=${group.guid}&idSystemApplication=${group.systemApplication.idSystemApplication}`,
		);
	}

	function changeStatus(group: any, option: any) {
		const { key } = option.status;

		if (group.active === true && key === StatusUserGroupEnum.Suspended) {
			group.active = false;
			usergroupApi.changeStatusUserGroup(group).then(() =>
				setGridData((prev) => {
					const clonePrev = lodash.cloneDeep(prev);
					const data = clonePrev.data.map((item) => {
						if (item.guid === group.guid) {
							return group;
						}
						return item;
					});
					return {
						...clonePrev,
						data,
					};
				}),
			);
		} else if (group.active === false && key === StatusUserGroupEnum.Active) {
			group.active = true;
			usergroupApi.changeStatusUserGroup(group).then(() =>
				setGridData((prev) => {
					const clonePrev = lodash.cloneDeep(prev);
					const data = clonePrev.data.map((item) => {
						if (item.guid === group.guid) {
							return group;
						}
						return item;
					});
					return {
						...clonePrev,
						data,
					};
				}),
			);
		}
	}

	function onLoadInfiniteScroll(request: UserGroupFilterRequest) {
		setIsLoadingInfiniteScroll(true);

		usergroupApi
			.getSearchGrid(request)
			.then((response) => {
				let resultData: GroupResponse[] = [];
				let resultTotalItems: number = 0;
				if (response.data && response.data.results && response.data.results.length > 0) {
					resultData = response.data.results;
					resultTotalItems = response.data.totalNumberOfRecords;
				}
				if (gridData.data.length > 0) {
					resultData = [...gridData.data, ...resultData];
				}
				setGridData({
					isLoading: false,
					totalItems: resultTotalItems,
					data: resultData,
				});

				// Disable show more
				if (resultTotalItems < PaginationEnum.PageSize10 || resultTotalItems === resultData.length) {
					setHasShowMore(false);
				}
			})
			.finally(() => setIsLoadingInfiniteScroll(false));
	}

	function onLoadSearchGrid() {
		const index = pageIndex + PaginationEnum.PageIndexIntial;
		setPageIndex(index);
		const formFiltersOnLoadSearchGrid = formFilters;
		if (formFiltersOnLoadSearchGrid) {
			const updatedFormFilters = {
				...formFiltersOnLoadSearchGrid,
				pageIndex: index,
			};
			onLoadInfiniteScroll(updatedFormFilters);
		}
	}

	useEffect(() => {
		setHasShowMore(gridData.data.length < gridData.totalItems);
	}, [gridData.data]);

	useEffect(() => {
		setPageIndex(PaginationEnum.PageIndexIntial);
	}, [formFilters]);

	const titleGrid = getDefaultMessageResultsFound(gridData.data.length, gridData.totalItems);
	const rowHeaders = [
		getTranslateByKey("information.companyType"),
		getTranslateByKey("information.company"),
		getTranslateByKey("information.userGroup"),
		getTranslateByKey("information.status"),
	];
	const sizeColumns = ["100px", "40%", "40%", "160px"];

	function renderColor(active: boolean) {
		switch (active) {
			case true:
				return "#00cc66";
			case false:
				return "#0099ff";
			default:
				return "#00cc66";
		}
	}

	if (gridData.isLoading) return <LoadingComponent />;
	if (gridData.data.length === 0 && formFilters === null) return null;
	if (gridData.data.length === 0 && formFilters !== null) return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
	if (gridData.data.length === 0 && !gridData.isLoading) return null;

	return (
		<S.Container>
			<BoxGrid
				title={titleGrid}
				sizeColumns={sizeColumns}
				rowHeaders={rowHeaders}
				infiniteScrollConfig={{
					dataLength: gridData.data.length,
					fetchData: () => onLoadSearchGrid(),
					hasMore: hasShowMore,
					isLoading: isLoadingInfiniteScroll,
				}}
			>
				{gridData.data.map((group) => (
					<KOGTableRow
						key={group.guid}
						id={`id-searchUser-tableRow-result-${group.name}`}
						onClick={(e) => handleRowBoxGrid(group, e)}
					>
						<TableCell>
							<TextEllipsis title={group.companyType?.name}>{group.companyType?.name}</TextEllipsis>
						</TableCell>
						<TableCell>
							<TextEllipsis title={group.companyData?.companyName}>{group.companyData?.companyName}</TextEllipsis>
						</TableCell>
						<TableCell>
							<TextEllipsis title={group.name}>{group.name}</TextEllipsis>
						</TableCell>
						<TableCell id="id-searchUserGroup-icon-status">
							<S.AreaStatus>
								<div
									className="d-flex align-items-center justify-content-center"
									style={{
										backgroundColor: renderColor(group?.active),
										height: 30,
										width: 100,
										borderRadius: 5,
										paddingLeft: "20px",
										paddingRight: "20px",
										color: "#fff",
										fontWeight: 500,
									}}
								>
									{group?.active
										? getTranslateByKey("statusRegister.active")
										: getTranslateByKey("statusRegister.suspend")}
								</div>
								<DropdownButton
									trigger={<MoreVertIcon className="ignore-click" id="id-searchUserGroup-icon-threeDots" />}
									options={optionsStatusUserGroup}
									status={group?.active}
									isGroup={true}
									disabled={group.hasOtherCompanyRelation}
									onClick={(option) => {
										changeStatus(group, option);
									}}
								/>
							</S.AreaStatus>
						</TableCell>
					</KOGTableRow>
				))}
			</BoxGrid>
		</S.Container>
	);
}

export default UsergroupSearchGrid;
